// v10/src/components/admin/inventory/InventoryDashboardHeader.js
//
// Stats consolidados del Stock de repuestos (A8) — Tarea 1 (lectura).
// Recibe el response de GET /api/inventory-items/dashboard y renderiza
// 3 mini-cards (total / stock bajo / serializados) + un grid con el
// top-N de categorías. El detalle de "Ver alertas" se conecta en Tarea 2.

import React from 'react';
import { Package, AlertTriangle, Hash } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getCategoryIcon } from './categoryIcons';

const TOP_CATEGORIES = 6;

const formatNum = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 0 });
};

const InventoryDashboardHeader = ({ stats, darkMode, locale = 'es-AR', onShowAlerts }) => {
  const { t } = useTranslation('admin');

  const totalItems        = Number(stats?.total_items) || 0;
  const lowStockCount     = Number(stats?.low_stock_count) || 0;
  const totalSerialized   = Number(stats?.total_serialized_items) || 0;
  const byCategory        = Array.isArray(stats?.by_category) ? stats.by_category : [];
  const serializedByStatus = Array.isArray(stats?.serialized_by_status) ? stats.serialized_by_status : [];

  const topCategories     = byCategory.slice(0, TOP_CATEGORIES);
  const remainingCount    = byCategory.length > TOP_CATEGORIES
    ? byCategory.slice(TOP_CATEGORIES).reduce((acc, c) => acc + (Number(c.count) || 0), 0)
    : 0;

  // Breakdown de S/N por status — sumamos solo los conocidos para el subtítulo.
  const serializedCounts = serializedByStatus.reduce(
    (acc, row) => {
      const c = Number(row?.count) || 0;
      if (row?.status === 'in_stock')  acc.in_stock  = c;
      if (row?.status === 'installed') acc.installed = c;
      if (row?.status === 'retired')   acc.retired   = c;
      return acc;
    },
    { in_stock: 0, installed: 0, retired: 0 },
  );

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';

  const cardBase = {
    padding: 16, borderRadius: 18,
    background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    display: 'flex', flexDirection: 'column', gap: 10, minHeight: 110,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Mini-cards superiores */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <StatMini
          icon={Package}
          label={t('inventory_dashboard_total_items')}
          value={formatNum(totalItems, locale)}
          color="#6366f1"
          darkMode={darkMode}
        />

        <StatMini
          icon={AlertTriangle}
          label={t('inventory_dashboard_low_stock')}
          value={formatNum(lowStockCount, locale)}
          color={lowStockCount > 0 ? '#ea580c' : '#16a34a'}
          darkMode={darkMode}
          highlight={lowStockCount > 0}
          ctaLabel={lowStockCount > 0 ? t('inventory_dashboard_view_alerts') : null}
          onCtaClick={lowStockCount > 0 ? onShowAlerts : null}
        />

        <StatMini
          icon={Hash}
          label={t('inventory_dashboard_serialized_items')}
          value={formatNum(totalSerialized, locale)}
          color="#0d9488"
          darkMode={darkMode}
          subtitle={
            totalSerialized > 0
              ? `${t('inventory_dashboard_serialized_in_stock')} ${formatNum(serializedCounts.in_stock, locale)}`
                + ` · ${t('inventory_dashboard_serialized_installed')} ${formatNum(serializedCounts.installed, locale)}`
                + (serializedCounts.retired > 0
                    ? ` · ${t('inventory_dashboard_serialized_retired')} ${formatNum(serializedCounts.retired, locale)}`
                    : '')
              : null
          }
        />
      </div>

      {/* Categorías */}
      {topCategories.length > 0 && (
        <div style={cardBase}>
          <p
            style={{
              margin: 0, fontSize: 11, fontWeight: 700, color: fgFaint,
              letterSpacing: '0.05em', textTransform: 'uppercase',
            }}
          >
            {t('inventory_dashboard_by_category')}
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 10,
            }}
          >
            {topCategories.map((row) => {
              const Icon = getCategoryIcon(row.category);
              const label = t(`inventory_categories_${row.category}`, { defaultValue: row.category });
              return (
                <div
                  key={row.category}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: 12,
                    background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                    border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                  }}
                >
                  <div
                    style={{
                      width: 32, height: 32, borderRadius: 10,
                      background: 'rgba(99,102,241,0.10)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon style={{ width: 16, height: 16, color: '#6366f1' }} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p
                      style={{
                        margin: 0, fontSize: 12, fontWeight: 700, color: fgMuted,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                      title={label}
                    >
                      {label}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 900, color: fgPrimary, lineHeight: 1.1 }}>
                      {formatNum(row.count, locale)}
                    </p>
                  </div>
                </div>
              );
            })}
            {remainingCount > 0 && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '10px 12px', borderRadius: 12,
                  background: darkMode ? 'rgba(255,255,255,0.02)' : 'white',
                  border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
                  color: fgMuted, fontSize: 12, fontWeight: 700,
                }}
                title={`+${remainingCount}`}
              >
                +{formatNum(remainingCount, locale)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StatMini = ({ icon: Icon, label, value, color, darkMode, subtitle = null, highlight = false, ctaLabel = null, onCtaClick = null }) => {
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: 16, borderRadius: 18,
        background: highlight
          ? `${color}10`
          : (darkMode ? 'rgba(255,255,255,0.03)' : 'white'),
        border: highlight
          ? `1px solid ${color}55`
          : (darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0'),
        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
        minHeight: 80,
      }}
    >
      <div
        style={{
          width: 44, height: 44, borderRadius: 14,
          background: `${color}1f`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon style={{ width: 22, height: 22, color }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <p style={{ margin: 0, fontSize: 24, fontWeight: 900, color: fgPrimary, lineHeight: 1, letterSpacing: '-0.4px' }}>
          {value}
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 12, fontWeight: 600, color: fgMuted, lineHeight: 1.3 }}>
          {label}
        </p>
        {subtitle && (
          <p
            style={{
              margin: '4px 0 0', fontSize: 11, color: fgMuted, lineHeight: 1.3,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
            title={subtitle}
          >
            {subtitle}
          </p>
        )}
        {ctaLabel && onCtaClick && (
          <button
            type="button"
            onClick={onCtaClick}
            style={{
              marginTop: 6, padding: '4px 10px', borderRadius: 999, border: 'none',
              cursor: 'pointer',
              background: color, color: 'white',
              fontFamily: 'inherit', fontSize: 11, fontWeight: 800,
            }}
          >
            {ctaLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default InventoryDashboardHeader;
